import * as React from "react";
import { Link } from "react-router";

import { AgentThread } from "@/AgentThread/AgentThreadSidebar/AgentThread";
import { AIAgentThreadProvider } from "@/AgentThread/AgentThreadSidebar/AIAgentThreadContext";
import { useNavigation } from "@/providers";
import { PageBreadcrumbs, PageLayout } from "@/shared/components";
import { ThreadTimestampFormatProvider } from "@/shared/components/RobotoMarkdown/timeFormat";
import { Goto } from "@/shared/navigation";

import styles from "./NewAgentThreadPage.module.css";

/**
 * A blank conversation on the full page at ``/agents/threads/new``, reached
 * from the Threads tab's "New thread" button.
 *
 * The thread detail page cannot show this itself: its address is
 * ``/agents/threads/:threadId``, which has no way to say "no thread yet". So a
 * new thread gets its own address, which also keeps reload and the back button
 * on the blank conversation while nothing has been sent.
 *
 * The first message creates the thread, and the page then replaces its URL
 * with that thread's, so the conversation continues on the detail page and
 * back skips the blank one. Until then there is no thread for a client tool to
 * run in, so unlike the detail page this one needs no sidebar handoff.
 */
export function NewAgentThreadPage() {
  const { goto } = useNavigation();

  const handleThreadIdChanged = React.useCallback(
    (nextId: string | undefined) => {
      if (nextId !== undefined) {
        goto.agentThread({ threadId: nextId }, { replace: true });
      }
    },
    [goto],
  );

  return (
    <PageLayout
      title="Roboto - New agent thread"
      headerContentLeft={
        <PageBreadcrumbs>
          <Link to={Goto.route.AgentThreads}>Agent Threads</Link>
          <span>New thread</span>
        </PageBreadcrumbs>
      }
    >
      <div className={styles.container}>
        <ThreadTimestampFormatProvider>
          <div className={styles.content}>
            <AIAgentThreadProvider
              threadId={undefined}
              onThreadIdChanged={handleThreadIdChanged}
            >
              <AgentThread />
            </AIAgentThreadProvider>
          </div>
        </ThreadTimestampFormatProvider>
      </div>
    </PageLayout>
  );
}
